import React, { useMemo } from 'react';
import cn from 'classnames';
import { useLocation } from '@reach/router';
import { GatsbyImage } from 'gatsby-plugin-image';
import Link from 'components/UI/Link';
import Image from 'components/UI/Image';
import Tag from 'components/UI/Tag';
import Hint from 'components/UI/Hint';
import Icon from 'components/UI/Icon';
import LazyImage from 'components/UI/LazyImage';
import { getImagesWithArtDirection } from 'utils/images';
import { useTrans, useLang } from 'utils/localisation';
import { pushCustomEvent } from 'utils/analytics';

import * as styles from './CustomProjectGalleryCard.module.scss';

export default function CustomProjectGalleryCard({
  img,
  imageDesktop,
  imageMobile,
  project_url,
  logo_color,
  logo,
  title,
  navItems,
  theme,
  isNda,
  ndaCustomerDescription,
  country,
}) {
  const t = useTrans(['common', 'nda']);
  const lang = useLang();
  const { pathname, state } = useLocation();
  const preparedProjectUrl =
    lang === 'az' || lang === 'ae' ? `https://andersenlab.com${project_url}` : project_url;
  const linkState = { prevPath: state?.prevPath ?? pathname, navItems };
  const image = useMemo(() => {
    const res = {};

    if (imageDesktop) {
      res.desktop = imageDesktop;
    }
    if (imageMobile) {
      res.mobile = imageMobile;
    }

    return !img && imageDesktop ? getImagesWithArtDirection(res) : img;
  }, [imageDesktop, imageMobile, img]);

  const handleClick = () => {
    pushCustomEvent('ndrsn_projects_block', {
      action: 'click_on_card',
    });
  };

  const ndaHint = (
    <div className={styles.hint}>
      {ndaCustomerDescription && (
        <div className={styles.hintText}>
          <span className={styles.hintLabel}>{t('customer')}:</span> {ndaCustomerDescription}
        </div>
      )}
      <div className={styles.hintText}>
        <span className={styles.hintLabel}>NDA:</span> {t('ndaCustomerText')}
      </div>
    </div>
  );

  return (
    <div className={cn(styles.item, theme && styles[theme])}>
      <Link
        className={styles.pictureWrapper}
        href={preparedProjectUrl}
        onClick={handleClick}
        state={linkState}
      >
        {img ? (
          <Image
            src={image}
            className={styles.picture}
            classImage={styles.img}
            alt={`${title} preview`}
          />
        ) : (
          <GatsbyImage
            image={image}
            className={styles.picture}
            imgClassName={styles.img}
            alt={title}
          />
        )}
        {country && (
          <div className={styles.imageLabels}>
            <Tag className={styles.label} theme="oval">
              {country}
            </Tag>
          </div>
        )}
      </Link>
      <div className={styles.content}>
        {isNda ? (
          <Hint content={ndaHint} placement="start" theme="nda">
            <div className={styles.nda}>
              <div className={styles.ndaLogoText}>nda</div>
              <Icon className={styles.ndaIcon} name="info-icon" />
            </div>
          </Hint>
        ) : (
          <>
            {logo_color && (
              <LazyImage src={logo_color} className={styles.logo} alt={`${title} logo`} />
            )}
            {!logo_color && logo && (
              <LazyImage src={logo} className={styles.logoWhite} alt={`${title} logo`} />
            )}
          </>
        )}
        <Link
          className={styles.title}
          href={preparedProjectUrl}
          onClick={handleClick}
          state={linkState}
        >
          {title}
        </Link>
        {country && (
          <div className={styles.contentLabels}>
            <Tag className={styles.label} theme="oval">
              {country}
            </Tag>
          </div>
        )}
      </div>
    </div>
  );
}
